// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
/**
 * Sprint 65.6.0 — DT-01 Training.
 *
 * The phase records DT-01 as a WORKFLOW GAP, and the measurement behind that
 * word is exact: before this file there was no training module, service, type,
 * collection, route or page. What looked like a journey was a button on the
 * monitoring programmes page whose entire behaviour was
 * `navigate('/admin/forms/templates?…')`, two pack form templates reachable
 * only through the generic form filler, and a `training` entry in
 * `MODULE_ROUTE_MAP` pointing at `/admin/training`, a route `App.tsx` never
 * declared.
 *
 * This is the contract the journey is built on. It is declared here first
 * because AC1's readiness measurement reads training completion, and a
 * component that reads a collection nobody has defined is how the fabricated
 * score got written in the first place.
 */
/** Where training assignments live. */
export const TRAINING_ASSIGNMENTS_COLLECTION = 'grc_training_assignments';
/** Where the courses those assignments point at live. */
export const TRAINING_COURSES_COLLECTION = 'grc_training_courses';
/**
 * The state of one person's assignment.
 *
 * `completed` is the only state that counts towards the readiness measurement,
 * and it is reachable only with recorded evidence — assignment alone is not
 * training, which is the distinction the workflow gap was about.
 */
export const TRAINING_ASSIGNMENT_STATUSES = [
    'assigned',
    'in_progress',
    'completed',
    'exempted',
    'cancelled',
];
/** How the completion was evidenced. */
export const TRAINING_EVIDENCE_KINDS = ['form_instance', 'attachment', 'attestation'];
/**
 * Sprint 65.6.0 (AC3) — the error codes the journey refuses with.
 *
 * Named codes rather than free text, for the reason 65.5.0's proof contract
 * gives: the refusal is the criterion. A caller that cannot tell *which* rule
 * refused it cannot act on the refusal, and a test asserting a message string
 * pins prose rather than behaviour.
 *
 * `TRAINING_COMPLETION_REQUIRES_EVIDENCE` is the whole point of the row being a
 * workflow gap rather than a Partial. Before this sprint the only thing
 * resembling a training journey was a button that navigated to a form browser:
 * nothing recorded who had to do the training, and nothing distinguished "this
 * person completed it" from "somebody said so". An assignment reaching
 * `completed` on its own say-so would rebuild exactly that.
 */
export const TRAINING_ERROR_CODES = {
    COURSE_NOT_FOUND: 'TRAINING_COURSE_NOT_FOUND',
    COURSE_INACTIVE: 'TRAINING_COURSE_INACTIVE',
    ASSIGNMENT_NOT_FOUND: 'TRAINING_ASSIGNMENT_NOT_FOUND',
    /** `completed` is unreachable without a `TrainingCompletionEvidence`. */
    COMPLETION_REQUIRES_EVIDENCE: 'TRAINING_COMPLETION_REQUIRES_EVIDENCE',
    /** An exemption is an attributed decision with a reason, never a flag. */
    EXEMPTION_REQUIRES_REASON: 'TRAINING_EXEMPTION_REQUIRES_REASON',
    /** `completed`, `exempted` and `cancelled` are terminal. */
    INVALID_TRANSITION: 'TRAINING_INVALID_TRANSITION',
    /** The same person cannot hold two open assignments for one course. */
    DUPLICATE_ASSIGNMENT: 'TRAINING_DUPLICATE_ASSIGNMENT',
};
/**
 * The legal transitions of one assignment.
 *
 * Declared as data rather than written as `if`s, so the three terminal states
 * are terminal by construction and a reader can see the whole machine at once —
 * the shape `CONTRACT_STATUS_TRANSITIONS` took in 65.5.0 after a status change
 * shipped as a bare assignment.
 */
export const TRAINING_ASSIGNMENT_TRANSITIONS = {
    assigned: ['in_progress', 'completed', 'exempted', 'cancelled'],
    in_progress: ['completed', 'exempted', 'cancelled'],
    completed: [],
    exempted: [],
    cancelled: [],
};
/** The statuses that still ask something of the assignee. */
export const TRAINING_OPEN_STATUSES = [
    'assigned',
    'in_progress',
];
//# sourceMappingURL=training.js.map